import "./App.scss";
import { ProductionsData, BakeryItem } from "./types";
import {
  convertTo12Hr,
  getAmPm,
  getTimeStamp,
  getArea,
  isPast
} from "./components/functions";
import HighRiskRotationBar from "./components/HighRiskRotationBar";
import React, { useState, useEffect } from "react";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import {
  joyrideStyles,
  joyrideStepsRotation
} from "./components/helpers-joyride";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/pro-solid-svg-icons";
import CountdownRefresh from "./components/CountdowRefresh";

interface HighRiskRotationProps {
  storeName: string | null;
  isFOH: boolean;
  isLoading: boolean;
  handleShowHome: () => void;
  bakeTableState: BakeryItem[];
  productionsData: ProductionsData[];
  triggerRefresh: () => void;
  onExpireCountdown: () => void;
}

const HighRiskRotation: React.FC<HighRiskRotationProps> = ({
  storeName,
  isFOH,
  handleShowHome,
  bakeTableState,
  productionsData,
  triggerRefresh,
  onExpireCountdown,
  isLoading
}) => {
  const [joyrideRun, setJoyrideRun] = useState<boolean>(false);
  const [hasExpired, setHasExpired] = useState(false);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    window.dataLayer.push({
      event: "Joyride High Risk Rotation",
      value: status,
      step: data.index.toString(),
      type: type,
      bakery: storeName,
      bakery_area: getArea(isFOH),
      time: getTimeStamp()
    });

    if (finishedStatuses.includes(status)) {
      setJoyrideRun(false);
    }
  };

const handleExpire = () => {
  setHasExpired(true);
  const now = new Date();
  const h = now.getHours();

  if (h >= 5 && h < 23) { // sync between 5am and 11pm
    onExpireCountdown();
    return;
  }

  const next5 = new Date(now);
  next5.setHours(5, 0, 0, 0); // reload at 5am
  if (next5 <= now) {
    next5.setDate(next5.getDate() + 1);
  }

  const msUntil5 = next5.getTime() - now.getTime();
  setTimeout(onExpireCountdown, msUntil5);
};

  return (
    <header className="App-header">
      <div className="header-bar">
        <h4 className="jr-rotation-name">High Risk Rotation</h4>
        {!hasExpired && (
          <CountdownRefresh
            duration={120}
            onExpire={handleExpire}
            title="Next sync in:"
          />
        )}
        <FontAwesomeIcon
          icon={faQuestion}
          className="risk-rotation__help jr-risk-rotation-help hlpr-cursor-pointer"
          onClick={() => {
            setJoyrideRun(true);
          }}
        />
      </div>
      <Joyride
        steps={joyrideStepsRotation}
        styles={joyrideStyles}
        run={joyrideRun}
        showProgress={false}
        continuous
        callback={handleJoyrideCallback}
        debug={false}
        disableScrolling
        showSkipButton
        hideCloseButton
        locale={{ skip: "Quit", last: "Finish" }}
      />

      <div className="scroll-table">
        <table
          className="bakery-table bakery-table--high-risk-rotation is-wide-view"
          cellSpacing="0"
        >
          <thead>
            <tr className="bakery-table__header">
              <th className="bakery-table__name-column">
                <div className="h4">Name</div>
              </th>
              <th className="centered jr-rotation-rotation">
                <div className="h4">Rotation</div>
              </th>
              <th className="centered jr-rotation-start">
                <div className="h4">Start</div>
              </th>
              <th className="centered jr-rotation-end">
                <div className="h4">End</div>
              </th>
              <th className="centered jr-rotation-qty">
                <div className="h4">QTY</div>
              </th>
              <th className="centered jr-rotation-temp">
                <div className="h4">Temp Check</div>
              </th>
              <th className="bakery-table__freshness-column jr-rotation-bar centered">
                <div className="h4">Rotation Bar</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {productionsData.length ? (
              productionsData.map((production, index) =>
                production.target_fresh || production.temp_check_temperature ? (
                  <tr
                    key={`${production.PLU}-${index}`}
                    className={`bakery-table__row${production.end_time && isPast(production.end_time)
                      ? " bakery-table__row--rotation-past"
                      : ""
                      }`}
                  >
                    <td>
                      <span className="h5">{production.product_name}</span>
                    </td>
                    <td className="centered">
                      {production.target_fresh &&
                        (production.target_fresh / 60).toPrecision(1) + "hr"}
                    </td>
                    <td className="centered">
                      {convertTo12Hr(
                        parseInt(production.timestamp.slice(11, 13))
                      )}
                      :{production.timestamp.slice(14, 16)}
                      <small>
                        {getAmPm(
                          parseInt(
                            convertTo12Hr(
                              parseInt(production.timestamp.slice(11, 13))
                            )
                          )
                        )}
                      </small>
                    </td>
                    <td className="centered end-time">
                      {production.end_time ? (
                        <>
                          {convertTo12Hr(
                            parseInt(production.end_time.slice(0, 2))
                          )}
                          :{production.end_time.slice(3, 5)}
                          <small>
                            {getAmPm(parseInt(production.end_time.slice(0, 2)))}
                          </small>
                        </>
                      ) : null}
                    </td>
                    <td className="centered">{production.product_units}</td>
                    <td className="centered">
                      {production.temp_check_temperature
                        ? `${production.temp_check_temperature}°C`
                        : ""}
                    </td>
                    <td className="centered">
                      {bakeTableState.filter(
                        (item) => item.PLU === production.PLU
                      )[0]?.target_fresh ? (
                        <HighRiskRotationBar
                          production={production}
                          bakeTableState={bakeTableState}
                          onExpireRefresh={triggerRefresh}
                        />
                      ) : null}
                    </td>
                  </tr>
                ) : null
              )
            ) : (
              !isLoading && (
                <tr className="bakery-table__row">
                  <td>
                    <p className="strong">Calm waters – look out for swell later</p>
                    <p>No waves submitted yet.</p>
                    <p>
                      If this doesn’t seem right, double‐check with your baker,
                      then contact KFP.
                    </p>
                    <p>
                      Return{" "}
                      <span
                        className="hlpr-text-underline"
                        onClick={handleShowHome}
                      >
                        home
                      </span>{" "}
                      to send to till.
                    </p>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </header>
  );
};

export default HighRiskRotation;
